// AuditReview - Pantalla de revisión final antes de completar la auditoría
// Sprint 3

import React, { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Paper,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
} from '@mui/icons-material';
import { useAuditState } from '../../hooks/useAuditState';
import auditService from '../../services/auditService';

const AuditReview: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const auditoriaId = parseInt(id || '0', 10);

  const [completing, setCompleting] = useState(false);
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);

  const {
    auditoria,
    formulario,
    respuestas,
    loading,
    error,
    getProgreso,
  } = useAuditState({
    auditoriaId,
  });

  const progreso = getProgreso();

  const handleBack = () => {
    navigate(`/audits/${auditoriaId}/execute`);
  };

  const handleComplete = async () => {
    setCompleting(true);
    try {
      await auditService.complete(auditoriaId, {
        signature_selfie_url: '', // TODO: Implementar captura de selfie
      });
      navigate('/audits', { 
        state: { 
          message: 'Auditoría completada exitosamente',
          severity: 'success'
        } 
      });
    } catch (err) {
      console.error('Error completando auditoría:', err);
      alert('Error al completar la auditoría');
    } finally {
      setCompleting(false);
      setShowConfirmDialog(false);
    }
  };

  if (loading) {
    return (
      <Container maxWidth="lg" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    );
  }

  if (error || !auditoria || !formulario) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">
          {error || 'No se pudo cargar la auditoría'}
        </Alert>
      </Container>
    );
  }

  const todasCompletas = progreso.porcentaje === 100;
  
  // Obtener todas las preguntas desde sections
  const todasLasPreguntas = (formulario.sections || []).flatMap((section: any) => 
    section.questions || []
  );
  
  const obligatoriasIncompletas = todasLasPreguntas.filter(
    (p: any) => p.required && !respuestas.has(p.id)
  );

  return (
    <Box sx={{ minHeight: '100vh', bgcolor: 'background.default', pb: 8 }}>
      {/* Header */}
      <Paper
        elevation={0}
        sx={{
          borderBottom: 1,
          borderColor: 'divider',
          position: 'sticky',
          top: 0,
          zIndex: 100,
          bgcolor: 'background.paper',
        }}
      >
        <Container maxWidth="lg">
          <Box sx={{ py: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
            <Button
              startIcon={<ArrowBackIcon />}
              onClick={handleBack}
              sx={{ minWidth: 'auto' }}
            >
              Volver
            </Button>
            <Box sx={{ flex: 1 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Revisión Final
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {(auditoria as any).business_name} - {(auditoria as any).form_name}
              </Typography>
            </Box>
          </Box>
        </Container>
      </Paper>

      <Container maxWidth="lg" sx={{ mt: 4 }}>
        {/* Resumen de progreso */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
            Resumen de Progreso
          </Typography>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
            {todasCompletas ? (
              <CheckCircleIcon color="success" sx={{ fontSize: 40 }} />
            ) : (
              <WarningIcon color="warning" sx={{ fontSize: 40 }} />
            )}
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 600 }}>
                {progreso.porcentaje}%
              </Typography>
              <Typography variant="body2" color="text.secondary">
                {progreso.respondidas} de {progreso.total} preguntas respondidas
              </Typography>
            </Box>
          </Box>

          {!todasCompletas && (
            <Alert severity="warning" sx={{ mt: 2 }}>
              {obligatoriasIncompletas.length > 0 ? (
                <>
                  Hay {obligatoriasIncompletas.length} pregunta{obligatoriasIncompletas.length !== 1 ? 's' : ''} obligatoria{obligatoriasIncompletas.length !== 1 ? 's' : ''} sin responder.
                  Por favor, completa todas las preguntas obligatorias antes de finalizar.
                </>
              ) : (
                <>
                  Algunas preguntas opcionales no han sido respondidas.
                  Puedes continuar o volver para completarlas.
                </>
              )}
            </Alert>
          )}
        </Paper>

        {/* Detalle por categoría */}
        <Paper sx={{ p: 3, mb: 3 }}>
          <Typography variant="h6" sx={{ mb: 3, fontWeight: 600 }}>
            Progreso por Categoría
          </Typography>

          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            {(formulario.categories || []).map((categoria: any) => {
              // Obtener todas las preguntas de todas las secciones
              const todasLasPreguntas = (formulario.sections || []).flatMap(
                (section: any) => section.questions || []
              );
              
              // Filtrar preguntas que pertenecen a esta categoría
              const preguntasDeCategoria = todasLasPreguntas.filter(
                (p: any) => p.category_id === categoria.id
              );
              
              const totalPreguntas = preguntasDeCategoria.length;
              const preguntasRespondidas = preguntasDeCategoria.filter((p: any) =>
                respuestas.has(p.id)
              ).length;
              const porcentaje = totalPreguntas > 0
                ? Math.round((preguntasRespondidas / totalPreguntas) * 100)
                : 0;
              const completa = preguntasRespondidas === totalPreguntas && totalPreguntas > 0;

              return (
                <Box
                  key={categoria.id}
                  sx={{
                    p: 2,
                    border: 1,
                    borderColor: completa ? 'success.main' : 'divider',
                    borderRadius: 1,
                    bgcolor: completa ? 'success.light' : 'background.paper',
                  }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography sx={{ fontWeight: 500 }}>
                      {categoria.name}
                    </Typography>
                    <Chip
                      label={completa ? 'Completa' : `${porcentaje}%`}
                      color={completa ? 'success' : 'default'}
                      size="small"
                      icon={completa ? <CheckCircleIcon /> : undefined}
                    />
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {preguntasRespondidas} de {totalPreguntas} preguntas respondidas
                  </Typography>
                </Box>
              );
            })}
          </Box>
        </Paper>

        {/* Acciones */}
        <Paper sx={{ p: 3 }}>
          <Box sx={{ display: 'flex', gap: 2, flexDirection: { xs: 'column', sm: 'row' } }}>
            <Button
              variant="outlined"
              onClick={handleBack}
              fullWidth
              sx={{ order: { xs: 2, sm: 1 } }}
            >
              Volver a editar
            </Button>
            <Button
              variant="contained"
              color="success"
              onClick={() => setShowConfirmDialog(true)}
              disabled={obligatoriasIncompletas.length > 0 || completing}
              fullWidth
              sx={{ order: { xs: 1, sm: 2 } }}
            >
              {completing ? 'Completando...' : 'Completar Auditoría'}
            </Button>
          </Box>

          {obligatoriasIncompletas.length > 0 && (
            <Alert severity="error" sx={{ mt: 2 }}>
              No se puede completar la auditoría hasta responder todas las preguntas obligatorias.
            </Alert>
          )}
        </Paper>
      </Container>

      {/* Dialog de confirmación */}
      <Dialog
        open={showConfirmDialog}
        onClose={() => setShowConfirmDialog(false)}
      >
        <DialogTitle>¿Completar Auditoría?</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Estás a punto de completar esta auditoría. Una vez completada, no podrás editar las respuestas.
            ¿Deseas continuar?
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowConfirmDialog(false)}>
            Cancelar
          </Button>
          <Button
            onClick={handleComplete}
            variant="contained"
            color="success"
            disabled={completing}
          >
            {completing ? 'Completando...' : 'Sí, Completar'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default AuditReview;
